import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Linkedin, Mail } from 'lucide-react';

// --- 1. DATA WITH PUBLIC_URL ---
const pgData = {
  faculty: [
    { name: "Dr. Rik Dey", role: "Faculty Coordinator", image: `${process.env.PUBLIC_URL}/pg/rik.webp`, linkedin: "https://www.linkedin.com/in/rik-dey-b16b9616/", email: "rikdey@iitk.ac.in" }
  ],
  overallHeads: [
    { name: "Debashish Nandi", role: "Overall Coordinator", image: `${process.env.PUBLIC_URL}/pg/debashish.webp`, linkedin: "https://www.linkedin.com/in/debashish-nandi-61456524a/", email: "debashish21@iitk.ac.in" },
    { name: "Kuldeep Kumar", role: "Overall Coordinator", image: `${process.env.PUBLIC_URL}/pg/kuldeep.webp`, linkedin: "https://www.linkedin.com/in/kuldeep-k-kushwaha-183315227", email: "kuldeepk21@iitk.ac.in" }
  ],
  managers: [
    { name: "Rishabh Bhat", role: "Manager, Career & Outreach", image: `${process.env.PUBLIC_URL}/pg/rishabh.webp`, linkedin: "https://www.linkedin.com/in/rishabhbhat/", email: "rishabhb24@iitk.ac.in" },
    { name: "Yawar Hayat Zarkob", role: "Manager, Web", image: `${process.env.PUBLIC_URL}/pg/yawar.webp`, linkedin: "https://www.linkedin.com/in/yawar-hayat-/", email: "yawarz21@iitk.ac.in" },
    { name: "Saksham Gupta", role: "Manager, Accounts", image: `${process.env.PUBLIC_URL}/pg/saksham.webp`, linkedin: "https://www.linkedin.com/in/saksham-gupta-a11974255", email: "sakshamg21@iitk.ac.in" },
    { name: "Akshay Kumar Jaiswal", role: "Manager, Events", image: `${process.env.PUBLIC_URL}/pg/akshay.webp`, linkedin: "https://www.linkedin.com/in/rahul-prakash-singh-4428977b", email: "akjaiswal23@iitk.ac.in" },
    { name: "Pankaj Kumar Barman", role: "Manager, Social Media", image: `${process.env.PUBLIC_URL}/pg/pankaj.webp`, linkedin: "https://www.linkedin.com/in/pankaj-kumar-barman-5a66761a7/", email: "pankajkb24@iitk.ac.in" },
    { name: "Durgesh B. Dongre", role: "Manager, Social Media", image: `${process.env.PUBLIC_URL}/pg/durgesh.webp`, linkedin: "https://in.linkedin.com/in/durgesh-dongre", email: "durgesh24@iitk.ac.in" }
  ]
};

// --- SKELETON COMPONENT ---
const MemberSkeleton = ({ isSmall }) => (
  <div className={`bg-slate-200 animate-pulse rounded-2xl ${isSmall ? 'w-60 h-80' : 'w-80 lg:w-96 h-[28rem]'}`} />
);

// --- 2. ANIMATION VARIANTS ---
const containerVariants = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.1 } } };
const itemVariants = { hidden: { y: 20, opacity: 0 }, visible: { y: 0, opacity: 1, transition: { duration: 0.2, ease: "easeOut" } } };

// --- 3. MEMBER CARD ---
const MemberCard = ({ name, role, image, linkedin, email, isSmall = false }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const cardHeight = isSmall ? 'h-80' : 'h-[28rem]';
  const cardWidth = isSmall ? 'w-60' : 'w-80 lg:w-96';

  return (
    <motion.div variants={itemVariants} whileHover={{ y: -10, scale: 1.02 }} className={`group relative rounded-2xl overflow-hidden shadow-2xl transition-all duration-300 ${cardWidth} ${cardHeight}`}>
      <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-500 to-blue-500 opacity-0 group-hover:opacity-40 blur-xl transition duration-500 z-0" />
      <div className="absolute inset-0 z-0 bg-slate-900">
        {!imageLoaded && image && <div className="absolute inset-0 bg-slate-800 animate-pulse" />}
        {image ? (
          <img src={image} alt={name} loading="lazy" onLoad={() => setImageLoaded(true)} className={`w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`} />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-800 to-slate-900 text-slate-500 font-bold text-5xl">{name.charAt(0)}</div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/60 to-transparent opacity-90 z-10" />
      </div>
      <div className="absolute inset-0 z-20 flex flex-col justify-end p-6 text-center">
        <h3 className={`font-bold text-white drop-shadow-md ${isSmall ? 'text-xl' : 'text-3xl'}`}>{name}</h3>
        <p className={`font-medium mt-2 text-slate-200 drop-shadow-sm ${isSmall ? 'text-sm' : 'text-lg'}`}>{role}</p>
        {(linkedin || email) && (
          <div className="flex gap-4 mt-5 justify-center md:opacity-0 md:transform md:translate-y-4 md:group-hover:opacity-100 md:group-hover:translate-y-0 transition-all duration-300">
            {linkedin && <a href={linkedin} target="_blank" rel="noopener noreferrer" className="p-2 rounded-full bg-white/20 text-white hover:bg-white/40 backdrop-blur-sm transition-colors border border-white/10"><Linkedin size={20} /></a>}
            {email && <a href={`mailto:${email}`} className="p-2 rounded-full bg-white/20 text-white hover:bg-white/40 backdrop-blur-sm transition-colors border border-white/10"><Mail size={20} /></a>}
          </div>
        )}
      </div>
    </motion.div>
  );
};

// --- 4. MAIN COMPONENT ---
const PGTeam = () => {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulated loading delay
    const timer = setTimeout(() => setIsLoading(false), 800);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 relative overflow-x-hidden pt-24 pb-20">
      <div className="absolute inset-0 z-0 opacity-[0.4]" style={{ backgroundImage: 'radial-gradient(#cbd5e1 1px, transparent 1px)', backgroundSize: '30px 30px' }} />
      <div className="absolute top-0 left-0 w-96 h-96 bg-purple-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
      <div className="absolute top-0 right-0 w-96 h-96 bg-blue-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>

      <div className="container mx-auto px-6 relative z-10">
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="text-center mb-20 max-w-3xl mx-auto">
          <h1 className="text-5xl md:text-6xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-slate-900 via-purple-800 to-slate-900 mb-6 drop-shadow-sm py-2 leading-tight">Postgraduate Team</h1>
          <p className="text-slate-600 text-xl font-light">Research scholars and masters students leading the way.</p>
        </motion.div>

        <AnimatePresence mode="wait">
          {isLoading ? (
            <div key="skeleton-container" className="space-y-24">
              <div>
                <div className="h-10 w-64 bg-slate-200 rounded-full mb-12 animate-pulse mx-auto md:mx-0" />
                <div className="flex justify-center"><MemberSkeleton /></div>
              </div>
              <div>
                <div className="h-10 w-64 bg-slate-200 rounded-full mb-12 animate-pulse mx-auto md:mx-0" />
                <div className="flex flex-wrap justify-center gap-10">
                  {[...Array(2)].map((_, i) => <MemberSkeleton key={i} />)}
                </div>
              </div>
            </div>
          ) : (
            <motion.div key="actual-content" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              {/* Faculty */}
              <div className="mb-24">
                <motion.h2 initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="text-3xl font-bold text-slate-800 mb-12 flex items-center justify-center md:justify-start gap-4">
                  <span className="w-2 h-8 bg-slate-600 rounded-full"></span>Faculty Coordinator
                </motion.h2>
                <motion.div variants={containerVariants} initial="hidden" animate="visible" className="flex justify-center">
                  {pgData.faculty.map((m, i) => <MemberCard key={i} {...m} />)}
                </motion.div>
              </div>

              {/* Overall Coordinators */}
              <div className="mb-24">
                <motion.h2 initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="text-3xl font-bold text-slate-800 mb-12 flex items-center justify-center md:justify-start gap-4">
                  <span className="w-2 h-8 bg-yellow-400 rounded-full"></span>Overall Coordinators
                </motion.h2>
                <motion.div variants={containerVariants} initial="hidden" animate="visible" className="flex flex-wrap justify-center gap-10">
                  {pgData.overallHeads.map((m, i) => <MemberCard key={i} {...m} />)}
                </motion.div>
              </div>

              {/* Managers */}
              <div>
                <motion.h2 initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="text-3xl font-bold text-slate-800 mb-12 flex items-center justify-center md:justify-start gap-4">
                  <span className="w-2 h-8 bg-purple-500 rounded-full"></span>Core Managers
                </motion.h2>
                <motion.div variants={containerVariants} initial="hidden" animate="visible" className="flex flex-wrap justify-center gap-10">
                  {pgData.managers.map((m, i) => <MemberCard key={i} {...m} />)}
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default PGTeam;